// WHATSAPP IN TRACK — the pure parts the screens share (17/10/2026).
// docs/whatsapp-contract.md is the contract; nothing here touches the DOM or
// Firestore, so every rule the screens depend on is tested on its own
// (test/wa-core.test.mjs).
//
//   phones     E.164 in, a masked number out ("+44 7··· ···412")
//   window     the 24-hour customer-service window from lastInboundAt
//   templates  a template body filled with its variables, for the preview
//   media      what a file is, from its name or MIME type

const HOUR = 3600e3;
const DAY = 24 * HOUR;

/** A Date from a Firestore Timestamp, a Date, an ISO string or millis; null when it is none of them. */
export function toDate(v) {
  if (v == null || v === '') return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v.toDate === 'function') { try { return v.toDate(); } catch (_) { return null; } }
  if (typeof v === 'object' && typeof v.seconds === 'number') return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

// ── Phones ─────────────────────────────────────────────────────────────────
/**
 * E.164 ("+447700900412") from however a number was typed. A UK number may be
 * written nationally (07700 900412); anything else must carry its country code
 * (+ or 00). Returns '' when it cannot be a phone number at all.
 */
export function toE164(raw, { country = '44' } = {}) {
  const s = String(raw == null ? '' : raw).trim();
  if (!s) return '';
  if (/[a-z]/i.test(s.replace(/^tel:/i, ''))) return '';
  let d = s.replace(/^tel:/i, '').replace(/[\s()\-.\u00a0\u202f\u200e\u200f\u202a-\u202e\u2066-\u2069]/g, '');
  if (d.startsWith('00')) d = `+${d.slice(2)}`;
  if (d.startsWith('+')) {
    d = `+${d.slice(1).replace(/^0+/, '')}`;
    // +44 (0)7700 … — the trunk zero some people keep after the country code.
    if (d.startsWith('+440')) d = `+44${d.slice(4)}`;
  } else if (d.startsWith('0')) {
    d = `+${country}${d.slice(1)}`;
  } else if (country === '44' && /^7\d{9}$/.test(d)) {
    d = `+44${d}`;
  } else {
    d = `+${d}`;
  }
  return isE164(d) ? d : '';
}

/** E.164: a plus, a non-zero digit, then 7–14 more digits (8–15 in all). UK mobiles are +447 and ten digits. */
export function isE164(s) {
  const v = String(s || '');
  if (!/^\+[1-9]\d{7,14}$/.test(v)) return false;
  if (v.startsWith('+44')) return /^\+44\d{10}$/.test(v);
  return true;
}

/** Meta's wa_id is the E.164 number without its plus. */
export const waIdOf = (e164) => String(e164 || '').replace(/^\+/, '');

/** "+44 7··· ···412": enough to recognise a number, not enough to copy it off a screen. */
export function maskPhone(raw) {
  const e = toE164(raw) || (isE164(raw) ? raw : '');
  if (!e) return String(raw || '');
  if (e.startsWith('+44')) return `+44 ${e.slice(3, 4)}··· ···${e.slice(-3)}`;
  return `${e.slice(0, 3)}··· ···${e.slice(-3)}`;
}

/** True when a WhatsApp export author is a bare number rather than a saved name. */
export const looksLikePhone = (s) => /^[+\d][\d\s()\-.\u00a0\u202f]{6,}$/.test(String(s || '').replace(/[\u200e\u200f]/g, '').trim());

/** The 1:1 thread id for a contact on one of our numbers (contract: `${phoneNumberId}_${waId}`). */
export const threadIdFor = (phoneNumberId, e164) => `${phoneNumberId}_${waIdOf(e164)}`;

// ── The 24-hour window ─────────────────────────────────────────────────────
/** "20h 41m", "41m", "under a minute". */
export function fmtLeft(ms) {
  const m = Math.floor(Math.max(0, ms) / 60000);
  if (m < 1) return 'under a minute';
  const h = Math.floor(m / 60);
  return h ? `${h}h ${m % 60}m` : `${m}m`;
}

/** "they last wrote 3 days ago" / "they last wrote yesterday" / "they have not written yet". */
export function lastWrote(last, now = new Date()) {
  const d = toDate(last);
  if (!d) return 'they have not written to ASP yet';
  const ms = now - d;
  if (ms < HOUR) return 'they last wrote under an hour ago';
  if (ms < DAY) { const h = Math.floor(ms / HOUR); return `they last wrote ${h} hour${h === 1 ? '' : 's'} ago`; }
  const days = Math.floor(ms / DAY);
  if (days === 1) return 'they last wrote yesterday';
  if (days < 60) return `they last wrote ${days} days ago`;
  return `they last wrote on ${d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`;
}

/**
 * Open iff now − lastInboundAt < 24 h (the contract's rule, to the millisecond).
 * { open, msLeft, closesIn: '20h 41m', hoursLeft: 20, since: 'they last wrote 3 days ago' }
 */
export function windowState(lastInboundAt, now = new Date()) {
  const d = toDate(lastInboundAt);
  const since = lastWrote(d, now);
  if (!d) return { open: false, msLeft: 0, closesIn: '', hoursLeft: 0, since };
  const msLeft = d.getTime() + DAY - now.getTime();
  const open = msLeft > 0;
  return { open, msLeft: open ? msLeft : 0, closesIn: open ? fmtLeft(msLeft) : '', hoursLeft: open ? Math.floor(msLeft / HOUR) : 0, since };
}

// ── Templates ──────────────────────────────────────────────────────────────
/**
 * A template body with its blanks filled, as parts the preview can bold:
 * [{ text, filled: false }, { text: 'the trial hole results', filled: true }, …].
 * Placeholders are Meta's positional {{1}}, {{2}} (in the order of
 * `variables`) or named {{key}}. A blank left empty shows its label in
 * brackets so the preview never claims a value nobody gave.
 */
export function fillTemplate(body, variables = [], values = {}) {
  const vars = Array.isArray(variables) ? variables : [];
  const parts = [];
  const src = String(body || '');
  let last = 0;
  const re = /\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g;
  let m;
  while ((m = re.exec(src))) {
    if (m.index > last) parts.push({ text: src.slice(last, m.index), filled: false });
    const k = m[1];
    const v = /^\d+$/.test(k) ? vars[Number(k) - 1] : vars.find((x) => x && x.key === k);
    const key = v ? v.key : k;
    const val = String((values && values[key]) || '').trim();
    parts.push(val ? { text: val, filled: true, key } : { text: `[${(v && v.label) || key}]`, filled: false, missing: true, key });
    last = re.lastIndex;
  }
  if (last < src.length) parts.push({ text: src.slice(last), filled: false });
  return { parts, text: parts.map((p) => p.text).join(''), missing: parts.filter((p) => p.missing).map((p) => p.key) };
}

/** The template's variables in send order ({{1}}, {{2}} …), as Meta wants them. */
export function orderedVars(variables = [], values = {}) {
  return (Array.isArray(variables) ? variables : []).map((v) => String((values && values[v.key]) || '').trim());
}

// ── Media ──────────────────────────────────────────────────────────────────
const EXT = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg', png: 'image/png', webp: 'image/webp', heic: 'image/heic', gif: 'image/gif',
  opus: 'audio/ogg', ogg: 'audio/ogg', m4a: 'audio/mp4', mp3: 'audio/mpeg', aac: 'audio/aac', amr: 'audio/amr', wav: 'audio/wav',
  mp4: 'video/mp4', mov: 'video/quicktime', '3gp': 'video/3gpp',
  pdf: 'application/pdf', doc: 'application/msword', docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel', xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  vcf: 'text/vcard', txt: 'text/plain', dwg: 'application/acad',
};
export const extOf = (name) => { const m = String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/); return m ? m[1] : ''; };
export const mimeFor = (name) => EXT[extOf(name)] || 'application/octet-stream';

/** 'image' | 'audio' | 'video' | 'document' | 'contacts' — from a MIME type or, failing that, a filename. */
export function mediaKind(mimeOrName) {
  const s = String(mimeOrName || '').toLowerCase();
  const mime = s.includes('/') ? s : mimeFor(s);
  if (mime.startsWith('image/')) return 'image';
  if (mime.startsWith('audio/')) return 'audio';
  if (mime.startsWith('video/')) return 'video';
  if (mime === 'text/vcard') return 'contacts';
  return 'document';
}

/** "YYYY-MM-DD" in local time (the by-date folder). */
export function dayFolder(d) {
  const x = toDate(d) || new Date();
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
}

/** The record-as chips, in the board's order, with the callable's kinds. */
export const RECORD_AS = [
  { kind: 'site-query', label: 'Site query' },
  { kind: 'instruction', label: 'Instruction' },
  { kind: 'variation', label: 'Variation' },
  { kind: 'call-note', label: 'Call note' },
  { kind: 'payment-chase', label: 'Payment chase' },
];
/** "Recorded as an instruction to confirm" and its kin. */
export function recordedLine(kind) {
  return ({
    instruction: 'Recorded as an instruction to confirm',
    'site-query': 'Recorded as a site query',
    variation: 'Recorded as a variation',
    'call-note': 'Recorded as a call note',
    'payment-chase': 'Recorded as a payment chase',
  })[kind] || (kind ? `Recorded as ${String(kind).replace(/-/g, ' ')}` : '');
}
